import { App as AntApp, Button, DatePicker, Form, Input, Select, Space } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';

import type { Passenger, PassengerInput, PassengerTitle } from '../../../src/types.ts';
import { api, errorText, type Country } from '../lib/api.ts';

interface Props {
  /** Editing an existing passenger; omit to create. */
  passenger?: Passenger | null;
  onSaved: (p: Passenger) => void;
  onCancel?: () => void;
}

interface FormValues {
  title: PassengerTitle;
  firstName: string;
  lastName: string;
  birthday: Dayjs;
  nationalityCode: string;
  passportNo: string;
  passportExpiryDate: Dayjs;
  passportIssueCode: string;
}

let countryCache: Country[] | null = null;

/** Countries as etravel lists them; the ISO-3 code is what passenger records store. */
export function useCountries(): { countries: Country[]; loading: boolean } {
  const [countries, setCountries] = useState<Country[]>(countryCache ?? []);
  const [loading, setLoading] = useState(!countryCache);
  useEffect(() => {
    if (countryCache) return;
    let cancelled = false;
    api
      .countries()
      .then((list) => {
        countryCache = (Array.isArray(list) ? list : []).filter((c) => c.iso3);
        if (!cancelled) setCountries(countryCache);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);
  return { countries, loading };
}

/**
 * Passenger details exactly as etravel's own form collects them: name as in
 * the passport, birthday (which decides adult/child/infant), nationality and
 * passport details.
 */
export function PassengerForm({ passenger, onSaved, onCancel }: Props) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<FormValues>();
  const [busy, setBusy] = useState(false);
  const { countries, loading } = useCountries();

  const countryOptions = countries.map((c) => ({ value: c.iso3!, label: `${c.nameEn ?? c.iso3} (${c.iso3})` }));

  const submit = async (v: FormValues) => {
    const body: PassengerInput = {
      title: v.title,
      firstName: v.firstName.trim().toUpperCase(),
      lastName: v.lastName.trim().toUpperCase(),
      birthday: v.birthday.format('YYYY-MM-DD'),
      nationalityCode: v.nationalityCode,
      passportNo: v.passportNo.trim().toUpperCase(),
      passportExpiryDate: v.passportExpiryDate.format('YYYY-MM-DD'),
      passportIssueCode: v.passportIssueCode,
    };
    setBusy(true);
    try {
      const saved = passenger ? await api.updatePassenger(passenger.id, body) : await api.createPassenger(body);
      message.success(passenger ? 'Passenger updated.' : 'Passenger added.');
      onSaved(saved && typeof saved === 'object' && 'id' in saved ? saved : { ...body, id: passenger?.id ?? 0 });
    } catch (err) {
      message.error(errorText(err), 6);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Form<FormValues>
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={(v) => void submit(v)}
      initialValues={
        passenger
          ? {
              title: passenger.title,
              firstName: passenger.firstName,
              lastName: passenger.lastName,
              birthday: passenger.birthday ? dayjs(passenger.birthday) : undefined,
              nationalityCode: passenger.nationalityCode,
              passportNo: passenger.passportNo,
              passportExpiryDate: passenger.passportExpiryDate ? dayjs(passenger.passportExpiryDate) : undefined,
              passportIssueCode: passenger.passportIssueCode,
            }
          : { title: 'MR' }
      }
    >
      <Space.Compact block>
        <Form.Item name="title" label="Title" style={{ flex: '0 0 96px' }} rules={[{ required: true }]}>
          <Select options={['MR', 'MRS', 'MISS'].map((t) => ({ value: t, label: t }))} />
        </Form.Item>
        <Form.Item name="firstName" label="First name (as in passport)" style={{ flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
          <Input placeholder="ABUBAKER" autoComplete="off" />
        </Form.Item>
        <Form.Item name="lastName" label="Last name" style={{ flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
          <Input placeholder="NAJI" autoComplete="off" />
        </Form.Item>
      </Space.Compact>
      <Space.Compact block>
        <Form.Item name="birthday" label="Date of birth" style={{ flex: 1 }} rules={[{ required: true, message: 'Required' }]} tooltip="Decides adult / child / infant fares.">
          <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs())} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="nationalityCode" label="Nationality" style={{ flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
          <Select showSearch optionFilterProp="label" loading={loading} options={countryOptions} placeholder="Libya (LBY)" />
        </Form.Item>
      </Space.Compact>
      <Space.Compact block>
        <Form.Item name="passportNo" label="Passport number" style={{ flex: 1 }} rules={[{ required: true, message: 'Required' }, { pattern: /^[A-Za-z0-9]+$/, message: 'Letters and digits only' }]}>
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item name="passportExpiryDate" label="Passport expiry" style={{ flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
          <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isBefore(dayjs())} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="passportIssueCode" label="Issuing country" style={{ flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
          <Select showSearch optionFilterProp="label" loading={loading} options={countryOptions} />
        </Form.Item>
      </Space.Compact>
      <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button type="primary" htmlType="submit" loading={busy}>
          {passenger ? 'Save changes' : 'Add passenger'}
        </Button>
      </Space>
    </Form>
  );
}
